import http, { request } from './http';
import type { Result } from './fileApi';
import {
  DEFAULT_FEATURE_MODULES,
  getFeatureModules,
  hasFeatureModulesCache,
  setFeatureModulesCache,
  type FeatureModules,
} from '@/utils/featureModules';

export type { FeatureModules, FeatureModuleKey } from '@/utils/featureModules';
export {
  DEFAULT_FEATURE_MODULES,
  getFeatureModules,
  isFeatureEnabled,
  featureModuleForApiPath,
} from '@/utils/featureModules';

function asBool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no') return false;
  }
  return fallback;
}

function normalize(raw: unknown): FeatureModules {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    knowledgeEnabled: asBool(o.knowledgeEnabled, DEFAULT_FEATURE_MODULES.knowledgeEnabled),
    aiToolsEnabled: asBool(o.aiToolsEnabled, DEFAULT_FEATURE_MODULES.aiToolsEnabled),
    projectManagementEnabled: asBool(
      o.projectManagementEnabled,
      DEFAULT_FEATURE_MODULES.projectManagementEnabled,
    ),
    smartDocumentEnabled: asBool(
      o.smartDocumentEnabled,
      DEFAULT_FEATURE_MODULES.smartDocumentEnabled,
    ),
  };
}

let inflight: Promise<FeatureModules> | null = null;

/** 拉取并缓存功能开关（可并行调用，只打一次请求） */
export async function ensureFeatureModules(force = false): Promise<FeatureModules> {
  if (!force && hasFeatureModulesCache()) return getFeatureModules();
  if (!force && inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await request<Result<FeatureModules>>(http.get('/app/features'));
      const r = res as unknown as Result<FeatureModules>;
      if (r?.success && r.data) {
        const next = normalize(r.data);
        setFeatureModulesCache(next);
        return next;
      }
      console.warn('[feature-modules] unexpected response, keep previous or defaults', r);
      if (!hasFeatureModulesCache()) {
        setFeatureModulesCache({ ...DEFAULT_FEATURE_MODULES });
      }
      return getFeatureModules();
    } catch (e) {
      console.warn('[feature-modules] fetch failed, using defaults until next success', e);
      if (!hasFeatureModulesCache()) {
        setFeatureModulesCache({ ...DEFAULT_FEATURE_MODULES });
      }
      return getFeatureModules();
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}
